import type { DataProvider } from "src/system/SystemProvider.tsx";
import { ResourceEnum } from "src/constants/constants.ts";
import { getExonnApi } from "src/exonn-api/exonn-api.tsx";
import {
  omitPathParameters,
  replacePathParameters,
} from "@exonn/js-sdk/src/utils.ts";
import type {
  CreateStockMoveFormMeta,
  CreateVariantFormMeta,
} from "src/framework/types.ts";
import { type ClientConfig } from "src/client-config/getClientConfig.ts";
import { OrderTabs } from "src/admin/modules/order/constants.ts";

const exceptions = (config: ClientConfig): Record<string, string> => {
  return {
    [ResourceEnum.B2bGateSupplier]: "/supplier-hub/suppliers",
  };
};

export const defaultDataProvider = (
  clientConfig: ClientConfig,
): DataProvider => ({
  getList: async (params) => {
    const {
      resource,
      page,
      sort,
      filter,
      paginationMode,
      groupKeys,
      signal,
      include,
      locale,
    } = params;

    const reqParams = {
      filter,
      sort,
      page,
      groupKeys,
      paginationMode,
      include,
    };
    const reqConfig = {
      signal,
      headers: {
        "X-Locale": locale,
      },
    };

    if (resource === ResourceEnum.AccountMovement) {
      const api = getExonnApi();
      return (await api.bill.getList(reqParams, null, reqConfig)).data;
    }
    const api = getExonnApi();

    return (
      await api.client.get(getModuleBaseUrl(resource, clientConfig), {
        params: reqParams,
        ...reqConfig,
      })
    ).data;
  },
  deleteOne: async (params) => {
    const { resource, id } = params;

    const reqParams = { id: id };

    const api = getExonnApi();
    const url = `${getModuleBaseUrl(resource, clientConfig)}/{id}`;
    return (
      await api.client.delete(replacePathParameters(url, reqParams), {
        params: omitPathParameters(reqParams, url),
        data: null,
      })
    ).data;
  },
  createOne: async (params): Promise<any> => {
    const { resource, data, locale, additionalHeaders } = params;
    const reqConfig = {
      headers: {
        "X-locale": locale,
        ...additionalHeaders,
      },
    };

    const baseUrl = exceptions(clientConfig)?.[resource] || `/${resource}`;
    const api = getExonnApi();
    const url = `${baseUrl}`;
    return (
      await api.client.post(replacePathParameters(url, null), data, {
        params: omitPathParameters(null, url),
        ...reqConfig,
      })
    ).data;
  },
  updateOne: async (params): Promise<any> => {
    const { resource, data, locale, id, additionalHeaders, tab } = params;
    const reqParams = {
      id,
    };
    const reqConfig = {
      headers: {
        "X-locale": locale,
        ...additionalHeaders,
      },
    };

    const getUpdateOneUrl = ({
      resource,
      clientConfig,
      tab,
    }: {
      resource: string;
      clientConfig: ClientConfig;
      tab?: string;
    }) => {
      if (resource === ResourceEnum.Document && tab === "addresses") {
        return `${getModuleBaseUrl(resource, clientConfig)}/{id}/address`;
      }
      if (resource === ResourceEnum.Document && tab === "texts") {
        return `${getModuleBaseUrl(resource, clientConfig)}/{id}/text`;
      }
      if (resource === ResourceEnum.Order && tab === OrderTabs.Address) {
        return `${getModuleBaseUrl(resource, clientConfig)}/{id}/address`;
      }
      return `${getModuleBaseUrl(resource, clientConfig)}/{id}`;
    };

    const getUpdateOneHttpMethod = ({
      resource,
      tab,
    }: {
      resource: string;
      tab?: string;
    }) => {
      if (resource === ResourceEnum.Document && tab === "addresses") {
        return "patch";
      }
      if (resource === ResourceEnum.Document && tab === "texts") {
        return "patch";
      }
      if (resource === ResourceEnum.Order && tab === OrderTabs.Address) {
        return "patch";
      }
      return "put";
    };

    const api = getExonnApi();
    const url = getUpdateOneUrl({
      resource,
      clientConfig,
      tab,
    });
    return (
      await api.client[
        getUpdateOneHttpMethod({
          resource,
          tab,
        })
      ](replacePathParameters(url, reqParams), data, {
        params: omitPathParameters(reqParams, url),
        ...reqConfig,
      })
    ).data;
  },
  getCreateFormData: async (params) => {
    const { resource, signal, locale, meta } = params;
    const reqConfig = {
      signal,
      headers: {
        "X-Locale": locale,
      },
    };
    const skip: string[] = [
      ResourceEnum.Category,
      ResourceEnum.Condition,
      ResourceEnum.Country,
      ResourceEnum.User,
      ResourceEnum.Role,
      ResourceEnum.Currency,
      ResourceEnum.VoucherSerie,
      ResourceEnum.Discount,
      ResourceEnum.Attribute,
      ResourceEnum.DeliveryMethod,
      ResourceEnum.CustomerGroup,
      ResourceEnum.Language,
      ResourceEnum.PaymentMethod,
      ResourceEnum.Owner,
      ResourceEnum.OwnerProfile,
      ResourceEnum.ProductGroup,
      ResourceEnum.ProcessingTime,
      ResourceEnum.PriceList,
      ResourceEnum.Vat,
      ResourceEnum.ConfigNumber,
      ResourceEnum.Document,
      ResourceEnum.B2bGateOrder,
      ResourceEnum.MarketingCompany,
      ResourceEnum.IntegrationToken,
    ];
    if (skip.includes(resource)) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({});
        }, 500);
      });
    }
    let reqParams = null;
    if (resource === ResourceEnum.StockMove) {
      reqParams = {
        variant_sku: (meta as CreateStockMoveFormMeta).variantSku,
      };
    }

    if (resource === ResourceEnum.Variant) {
      reqParams = {
        product_id: (meta as CreateVariantFormMeta).product_id,
      };
    }

    const api = getExonnApi();
    const url = `${getModuleBaseUrl(resource, clientConfig)}/new-form`;
    return (
      await api.client.get(url, {
        params: reqParams,
        data: null,
        ...reqConfig,
      })
    ).data;
  },
  getOne: async (params) => {
    const { resource, id, include, signal, locale } = params;
    const reqConfig = {
      signal,
      headers: {
        "X-Locale": locale,
      },
    };
    const reqParams = { id, include };

    const api = getExonnApi();
    const url = `${getModuleBaseUrl(resource, clientConfig)}/{id}`;
    return (
      await api.client.get(replacePathParameters(url, reqParams), {
        params: omitPathParameters(reqParams, url),
        data: null,
        ...reqConfig,
      })
    ).data;
  },
  cloneToLocale: async ({ resource, id, to_locale, from_locale }) => {
    const api = getExonnApi();
    return api.system.resource.copyLocale(
      {},
      {
        resource: resource,
        from_locale: from_locale,
        to_locale: to_locale,
        id: id,
      },
    );
  },
});

const getModuleBaseUrl = (resource: string, config: ClientConfig) => {
  return exceptions(config)?.[resource] || `/${resource}`;
};
